import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Divider,
  Alert,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Avatar,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  ListItemIcon,
  Fab,
  Pagination,
  Collapse,
  Stack
} from '@mui/material';

import {
  Scanner as ScannerIcon,
  Settings as SettingsIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  PlayArrow as TestIcon,
  Save as SaveIcon,
  Refresh as RefreshIcon,
  ExpandMore as ExpandMoreIcon,
  Science as ScienceIcon,
  Biotech as BiotechIcon,
  LocalHospital as HospitalIcon,
  Close as CloseIcon,
  Category as CategoryIcon,
  Tune as TuneIcon
} from '@mui/icons-material';

import { styled } from '@mui/material/styles';
import { loadAllScansWithParameters, addScanCategory, updateScanCategory, deleteScanCategory, addScanParameter, updateScanParameter, deleteScanParameter } from '../BaseURL';

const StyledCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: theme.shadows[8],
  },
}));

const StyledAccordion = styled(Accordion)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  borderRadius: theme.spacing(1),
  '&:before': {
    display: 'none',
  },
  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
}));

const SetupScan = () => {
  const [labCategories, setLabCategories] = useState([]);
  const [filteredCategories, setFilteredCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedParameter, setSelectedParameter] = useState(null);
  const [isCategoryDialogOpen, setIsCategoryDialogOpen] = useState(false);
  const [isParameterDialogOpen, setIsParameterDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState('add'); 
  const [categoryFormData, setCategoryFormData] = useState({
    name: '',
    description: ''
  });
  const [parameterFormData, setParameterFormData] = useState({
    name: '',
    description: ''
  });
  const [parametersList, setParametersList] = useState([{ id: Date.now(), name: '', description: '' }]);
  const [loading, setLoading] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState(new Set());
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Load scan data from API
  useEffect(() => {
    const loadScanData = async () => {
      setLoading(true);
      try {
        const response = await axios.get(loadAllScansWithParameters());
        if (response.data && response.data.success) {
          // Transform the data to match the expected format
          const transformedCategories = response.data.data.map((scan, index) => ({
            id: index + 1,
            name: scan.scan,
            description: scan.scan,
            parameters: scan.parameters ? scan.parameters.map((param) => ({
              id: param.id,
              name: param.paramater,
              description: param.Flag || '-' // Use Flag field as description/findings
            })) : []
          }));
          setLabCategories(transformedCategories);
          setFilteredCategories(transformedCategories);
        } else {
          console.error('Failed to load scan data:', response.data?.message);
          setLabCategories([]);
          setFilteredCategories([]);
        }
      } catch (error) {
        console.error('Error loading scan data:', error);
        setLabCategories([]);
        setFilteredCategories([]);
      } finally {
        setLoading(false);
      }
    };

    loadScanData();
  }, []);

  // Filter categories based on search term
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredCategories(labCategories);
    } else {
      const filtered = labCategories.filter(category => {
        const categoryMatch = category.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                             category.description.toLowerCase().includes(searchTerm.toLowerCase());
        
        const parameterMatch = category.parameters.some(param => 
          param.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          param.description.toLowerCase().includes(searchTerm.toLowerCase())
        );
        
        return categoryMatch || parameterMatch;
      });
      setFilteredCategories(filtered);
    }
    setPage(1); // Reset to first page when filtering changes
  }, [searchTerm, labCategories]);

  const handleCategoryDialogOpen = (mode, category = null) => {
    setDialogMode(mode);
    if (mode === 'edit' && category) {
      setCategoryFormData({
        name: category.name,
        description: category.description
      });
      setSelectedCategory(category);
    } else {
      setCategoryFormData({
        name: '',
        description: ''
      });
      setSelectedCategory(null);
    }
    setIsCategoryDialogOpen(true);
  };

  const handleCategoryDialogClose = () => {
    setIsCategoryDialogOpen(false);
    setSelectedCategory(null);
    setCategoryFormData({
      name: '',
      description: ''
    });
  };

  const handleParameterDialogOpen = (mode, categoryId, parameter = null) => {
    setDialogMode(mode);
    const category = labCategories.find(cat => cat.id === categoryId);
    setSelectedCategory({ id: categoryId, name: category?.name });
    setSelectedParameter(parameter);
    if (mode === 'edit' && parameter) {
      setParametersList([{ id: Date.now(), name: parameter.name, description: parameter.description }]);
    } else {
      setParametersList([{ id: Date.now(), name: '', description: '' }]);
    }
    setIsParameterDialogOpen(true);
  };

  const handleParameterDialogClose = () => {
    setIsParameterDialogOpen(false);
    setSelectedCategory(null);
    setSelectedParameter(null);
    setParametersList([{ id: Date.now(), name: '', description: '' }]);
  };

  const handleAddParameterField = () => {
    setParametersList([...parametersList, { id: Date.now(), name: '', description: '' }]);
  };

  const handleRemoveParameterField = (id) => {
    if (parametersList.length > 1) {
      setParametersList(parametersList.filter(param => param.id !== id));
    }
  };

  const handleParameterChange = (id, field, value) => {
    setParametersList(parametersList.map(param => 
      param.id === id ? { ...param, [field]: value } : param
    ));
  };

  const handleSaveCategory = async () => {
    setLoading(true);
    try {
      if (dialogMode === 'add') {
        const response = await axios.post(addScanCategory, {
          scanName: categoryFormData.name
        });

        if (response.data.success) {
          // Reload data after successful save
          const loadResponse = await axios.get(loadAllScansWithParameters());
          if (loadResponse.data && loadResponse.data.success) {
            const transformedCategories = loadResponse.data.data.map((scan, index) => ({
              id: index + 1,
              name: scan.scan,
              description: scan.scan,
              parameters: scan.parameters ? scan.parameters.map((param) => ({
                id: param.id,
                name: param.paramater,
                description: param.Flag || '-' // Use Flag field as description/findings
              })) : []
            }));
            setLabCategories(transformedCategories);
            setFilteredCategories(transformedCategories);
          }
        } else {
          console.error('Failed to add category:', response.data.message);
        }
      } else if (dialogMode === 'edit' && selectedCategory) {
        const response = await axios.put(updateScanCategory(selectedCategory.name), {
          newScanName: categoryFormData.name
        });

        if (response.data.success) {
          // Reload data after successful update
          const loadResponse = await axios.get(loadAllScansWithParameters());
          if (loadResponse.data && loadResponse.data.success) {
            const transformedCategories = loadResponse.data.data.map((scan, index) => ({
              id: index + 1,
              name: scan.scan,
              description: scan.scan,
              enabled: scan.Flag === '1' || scan.Flag === 1,
              parameters: scan.parameters ? scan.parameters.map((param) => ({
                id: param.id,
                name: param.paramater,
                description: param.Flag || '-', // Use Flag field as description/findings
                enabled: param.Flag === '1' || param.Flag === 1
              })) : []
            }));
            setLabCategories(transformedCategories);
            setFilteredCategories(transformedCategories);
          }
        } else {
          console.error('Failed to update category:', response.data.message);
        }
      }
      handleCategoryDialogClose();
    } catch (error) {
      console.error('Error saving category:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveParameter = async () => {
    // Validate that all parameters have names
    const validParameters = parametersList.filter(param => param.name.trim() !== '');
    if (validParameters.length === 0) return;

    setLoading(true);
    try {
      if (dialogMode === 'add') {
        // Save multiple parameters
        const savePromises = validParameters.map(param => 
          axios.post(addScanParameter, {
            scanName: selectedCategory.name,
            parameterName: param.name,
            flag: param.description || '-' // Use description as flag/findings
          })
        );

        await Promise.all(savePromises);
      } else if (dialogMode === 'edit' && selectedParameter && validParameters.length === 1) {
        const response = await axios.put(updateScanParameter(selectedParameter.id), {
          parameterName: validParameters[0].name,
          flag: validParameters[0].description || '-' // Use description as flag/findings
        });

        if (!response.data.success) {
          console.error('Failed to update parameter:', response.data.message);
        }
      }

      // Reload data after successful save
      const loadResponse = await axios.get(loadAllScansWithParameters());
      if (loadResponse.data && loadResponse.data.success) {
        const transformedCategories = loadResponse.data.data.map((scan, index) => ({
          id: index + 1,
          name: scan.scan,
          description: scan.scan,
          enabled: scan.Flag === '1' || scan.Flag === 1,
          parameters: scan.parameters ? scan.parameters.map((param) => ({
            id: param.id,
            name: param.paramater,
            description: param.paramater,
            enabled: param.Flag === '1' || param.Flag === 1
          })) : []
        }));
        setLabCategories(transformedCategories);
        setFilteredCategories(transformedCategories);
      }

      handleParameterDialogClose();
    } catch (error) {
      console.error('Error saving parameters:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    const category = labCategories.find(cat => cat.id === categoryId);
    if (!category) return;

    if (window.confirm(`Are you sure you want to delete the "${category.name}" scan category? This will also delete all its parameters.`)) {
      try {
        const response = await axios.delete(deleteScanCategory(category.name));
        if (response.data.success) {
          // Reload data after successful deletion
          const loadResponse = await axios.get(loadAllScansWithParameters());
          if (loadResponse.data && loadResponse.data.success) {
            const transformedCategories = loadResponse.data.data.map((scan, index) => ({
              id: index + 1,
              name: scan.scan,
              description: scan.scan,
              enabled: scan.Flag === '1' || scan.Flag === 1,
              parameters: scan.parameters ? scan.parameters.map((param) => ({
                id: param.id,
                name: param.paramater,
                description: param.paramater,
                enabled: param.Flag === '1' || param.Flag === 1
              })) : []
            }));
            setLabCategories(transformedCategories);
            setFilteredCategories(transformedCategories);
          }
        } else {
          console.error('Failed to delete category:', response.data.message);
        }
      } catch (error) {
        console.error('Error deleting category:', error);
      }
    }
  };

  const handleDeleteParameter = async (categoryId, parameterId) => {
    const category = labCategories.find(cat => cat.id === categoryId);
    const parameter = category?.parameters.find(param => param.id === parameterId);

    if (!parameter) return;

    if (window.confirm(`Are you sure you want to delete the "${parameter.name}" parameter?`)) {
      try {
        const response = await axios.delete(deleteScanParameter(parameterId));
        if (response.data.success) {
          // Reload data after successful deletion
          const loadResponse = await axios.get(loadAllScansWithParameters());
          if (loadResponse.data && loadResponse.data.success) {
            const transformedCategories = loadResponse.data.data.map((scan, index) => ({
              id: index + 1,
              name: scan.scan,
              description: scan.scan,
              enabled: scan.Flag === '1' || scan.Flag === 1,
              parameters: scan.parameters ? scan.parameters.map((param) => ({
                id: param.id,
                name: param.paramater,
                description: param.Flag || '-', // Use Flag field as description/findings
                enabled: param.Flag === '1' || param.Flag === 1
              })) : []
            }));
            setLabCategories(transformedCategories);
            setFilteredCategories(transformedCategories);
          }
        } else {
          console.error('Failed to delete parameter:', response.data.message);
        }
      } catch (error) {
        console.error('Error deleting parameter:', error);
      }
    }
  };

  const handleToggleCategory = (categoryId) => {
    setExpandedCategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(categoryId)) {
        newSet.delete(categoryId);
      } else {
        newSet.add(categoryId);
      }
      return newSet;
    });
  };

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  const getCategoryIcon = (categoryName) => {
    if (categoryName.includes('SOFT TISSUE')) return <BiotechIcon />;
    if (categoryName.includes('HEMATOLOGY')) return <HospitalIcon />;
    return <ScienceIcon />;
  };

  return (
    <Box sx={{ p: 3, maxWidth: '100%' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <ScannerIcon sx={{ fontSize: 32, color: 'primary.main' }} />
          <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#1e293b' }}>
            Setup Scan
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleCategoryDialogOpen('add')}
          sx={{ borderRadius: 2 }}
        >
          Add Category
        </Button>
      </Box>

      <Typography variant="body1" sx={{ color: '#64748b', mb: 4 }}>
        Configure laboratory test categories and their parameters
      </Typography>

      {/* Search Bar */}
      <Box sx={{ mb: 3 }}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Search categories or parameters..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <Box sx={{ mr: 1, color: 'text.secondary' }}>
                🔍
              </Box>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
            }
          }}
        />
      </Box>

      {/* Scan Categories — Compact 3-column card grid */}
      <Box sx={{ mb: 4 }}>
        <Grid container spacing={2} alignItems="flex-start">
          {filteredCategories
            .slice((page - 1) * itemsPerPage, page * itemsPerPage)
            .map((category) => {
              const isOpen = expandedCategories.has(category.id);
              return (
                <Grid item xs={12} sm={6} md={4} key={category.id}>
                  <Card
                    sx={{
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: isOpen ? 'primary.main' : 'divider',
                      boxShadow: isOpen ? 4 : 1,
                      transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
                      overflow: 'hidden'
                    }}
                  >
                    {/* Compact clickable header */}
                    <Box
                      onClick={() => handleToggleCategory(category.id)}
                      sx={{
                        px: 1.5,
                        py: 1.25,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        cursor: 'pointer',
                        bgcolor: isOpen ? 'primary.lighter' : 'transparent',
                        transition: 'background-color 0.2s ease',
                        '&:hover': {
                          bgcolor: isOpen ? 'primary.lighter' : 'action.hover'
                        }
                      }}
                    >
                      <Avatar
                        sx={{
                          bgcolor: 'primary.main',
                          width: 32,
                          height: 32,
                          '& .MuiSvgIcon-root': { fontSize: 18 }
                        }}
                      >
                        {getCategoryIcon(category.name)}
                      </Avatar>
                      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 600,
                            lineHeight: 1.2,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                          title={category.name}
                        >
                          {category.name}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ color: 'text.secondary' }}
                        >
                          {category.parameters.length} parameter{category.parameters.length === 1 ? '' : 's'}
                        </Typography>
                      </Box>
                      <Stack direction="row" spacing={0.25} alignItems="center">
                        <Tooltip title="Edit category">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleCategoryDialogOpen('edit', category);
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete category">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteCategory(category.id);
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <IconButton
                          size="small"
                          sx={{
                            transition: 'transform 0.25s ease',
                            transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)'
                          }}
                        >
                          <ExpandMoreIcon fontSize="small" />
                        </IconButton>
                      </Stack>
                    </Box>

                    {/* Collapsible details */}
                    <Collapse in={isOpen} timeout="auto" unmountOnExit>
                      <Divider />
                      <Box sx={{ p: 1.5, bgcolor: 'grey.50' }}>
                        {category.description && (
                          <Typography
                            variant="caption"
                            sx={{ display: 'block', color: 'text.secondary', mb: 1.5, fontStyle: 'italic' }}
                          >
                            {category.description}
                          </Typography>
                        )}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                          <Typography variant="overline" sx={{ fontWeight: 700, color: 'text.secondary', letterSpacing: 0.5 }}>
                            Parameters
                          </Typography>
                          <Button
                            size="small"
                            variant="text"
                            startIcon={<AddIcon fontSize="small" />}
                            onClick={() => handleParameterDialogOpen('add', category.id)}
                            sx={{ fontSize: '0.72rem', py: 0.25 }}
                          >
                            Add
                          </Button>
                        </Box>

                        {category.parameters.length === 0 ? (
                          <Box sx={{ textAlign: 'center', py: 2 }}>
                            <ScienceIcon sx={{ fontSize: 32, color: 'grey.400', mb: 0.5 }} />
                            <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mb: 1 }}>
                              No parameters yet
                            </Typography>
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<AddIcon fontSize="small" />}
                              onClick={() => handleParameterDialogOpen('add', category.id)}
                            >
                              Add First
                            </Button>
                          </Box>
                        ) : (
                          <Stack spacing={0.75}>
                            {category.parameters.map((parameter) => (
                              <Box
                                key={parameter.id}
                                sx={{
                                  p: 1,
                                  bgcolor: 'background.paper',
                                  border: '1px solid',
                                  borderColor: 'divider',
                                  borderRadius: 1,
                                  display: 'flex',
                                  alignItems: 'flex-start',
                                  gap: 1,
                                  transition: 'border-color 0.15s ease',
                                  '&:hover': { borderColor: 'primary.light' }
                                }}
                              >
                                <ScienceIcon color="primary" sx={{ fontSize: 16, mt: 0.25 }} />
                                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                                  <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                                    {parameter.name}
                                  </Typography>
                                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                                    {parameter.description && parameter.description !== '-' ? (
                                      parameter.description.split(',').map((flag, idx) => (
                                        <Chip
                                          key={idx}
                                          label={flag.trim()}
                                          size="small"
                                          color={flag.trim() === 'normal' || flag.trim() === '-' ? 'success' : 'warning'}
                                          variant="outlined"
                                          sx={{ fontSize: '0.65rem', height: 18 }}
                                        />
                                      ))
                                    ) : (
                                      <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                        No flags configured
                                      </Typography>
                                    )}
                                  </Box>
                                </Box>
                                <Stack direction="row" spacing={0}>
                                  <Tooltip title="Edit parameter">
                                    <IconButton
                                      size="small"
                                      onClick={() => handleParameterDialogOpen('edit', category.id, parameter)}
                                      sx={{ p: 0.5 }}
                                    >
                                      <EditIcon sx={{ fontSize: 14 }} />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Delete parameter">
                                    <IconButton
                                      size="small"
                                      color="error"
                                      onClick={() => handleDeleteParameter(category.id, parameter.id)}
                                      sx={{ p: 0.5 }}
                                    >
                                      <DeleteIcon sx={{ fontSize: 14 }} />
                                    </IconButton>
                                  </Tooltip>
                                </Stack>
                              </Box>
                            ))}
                          </Stack>
                        )}
                      </Box>
                    </Collapse>
                  </Card>
                </Grid>
              );
            })}
        </Grid>
      </Box>

      {/* Pagination */}
      {filteredCategories.length > itemsPerPage && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}>
          <Pagination
            count={Math.ceil(filteredCategories.length / itemsPerPage)}
            page={page}
            onChange={handlePageChange}
            color="primary"
            size="large"
            showFirstButton
            showLastButton
          />
        </Box>
      )}

      {/* Empty State */}
      {labCategories.length === 0 && (
        <Paper sx={{ p: 6, textAlign: 'center', mt: 3 }}>
          <ScannerIcon sx={{ fontSize: 64, color: 'grey.400', mb: 2 }} />
          <Typography variant="h6" sx={{ mb: 1, color: 'grey.600' }}>
            No Lab Categories Configured
          </Typography>
          <Typography variant="body2" sx={{ mb: 3, color: 'grey.500' }}>
            Add your first lab test category to start configuring parameters
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleCategoryDialogOpen('add')}
          >
            Add Category
          </Button>
        </Paper>
      )}

      {/* Add/Edit Category Dialog */}
      <Dialog
        open={isCategoryDialogOpen}
        onClose={handleCategoryDialogClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: 'hidden',
            boxShadow: '0 20px 60px rgba(15, 23, 42, 0.18)',
          },
        }}
      >
        {/* Header */}
        <Box
          sx={{
            px: 3,
            py: 2.25,
            background: 'linear-gradient(135deg, #1A9B7F 0%, #157763 100%)',
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
          }}
        >
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'rgba(255,255,255,0.18)',
              backdropFilter: 'blur(8px)',
              flexShrink: 0,
            }}
          >
            <CategoryIcon sx={{ fontSize: 22 }} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: '1.05rem', fontWeight: 700, lineHeight: 1.2 }}>
              {dialogMode === 'add' ? 'Add Scan Category' : 'Edit Scan Category'}
            </Typography>
            <Typography sx={{ fontSize: '0.78rem', opacity: 0.88, mt: 0.25 }}>
              {dialogMode === 'add'
                ? 'Create a new scan category to group related parameters.'
                : 'Update the category details below.'}
            </Typography>
          </Box>
          <IconButton
            onClick={handleCategoryDialogClose}
            size="small"
            sx={{ color: '#fff', '&:hover': { bgcolor: 'rgba(255,255,255,0.16)' } }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <DialogContent sx={{ p: 3, bgcolor: '#fff' }}>
          <Stack spacing={2.25} sx={{ mt: 0.5 }}>
            <Box>
              <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.5px', mb: 0.75 }}>
                Category Name <Box component="span" sx={{ color: '#dc2626' }}>*</Box>
              </Typography>
              <TextField
                fullWidth
                size="small"
                value={categoryFormData.name}
                onChange={(e) => setCategoryFormData({ ...categoryFormData, name: e.target.value })}
                placeholder="e.g., SOFT TISSUE (SPECIFICATION)"
                required
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.75, bgcolor: '#fff' } }}
              />
            </Box>
            <Box>
              <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.5px', mb: 0.75 }}>
                Description
              </Typography>
              <TextField
                fullWidth
                size="small"
                value={categoryFormData.description}
                onChange={(e) => setCategoryFormData({ ...categoryFormData, description: e.target.value })}
                multiline
                rows={3}
                placeholder="Brief description of this scan category"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.75, bgcolor: '#fff' } }}
              />
            </Box>
          </Stack>
        </DialogContent>

        <DialogActions
          sx={{
            px: 3,
            py: 2,
            bgcolor: '#f8fafc',
            borderTop: '1px solid #e5e9ef',
            gap: 1,
          }}
        >
          <Button
            onClick={handleCategoryDialogClose}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              color: '#475569',
              px: 2.5,
              '&:hover': { bgcolor: '#e2e8f0' },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSaveCategory}
            variant="contained"
            disabled={loading || !categoryFormData.name}
            startIcon={loading ? <RefreshIcon /> : <SaveIcon />}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              px: 3,
              borderRadius: 1.75,
              boxShadow: 'none',
              background: 'linear-gradient(135deg, #1A9B7F 0%, #157763 100%)',
              '&:hover': {
                boxShadow: '0 4px 12px rgba(26, 155, 127, 0.3)',
                background: 'linear-gradient(135deg, #157763 0%, #126553 100%)',
              },
            }}
          >
            {loading ? 'Saving…' : (dialogMode === 'add' ? 'Create Category' : 'Save Changes')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add/Edit Parameter Dialog */}
      <Dialog
        open={isParameterDialogOpen}
        onClose={handleParameterDialogClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: 'hidden',
            boxShadow: '0 20px 60px rgba(15, 23, 42, 0.18)',
          },
        }}
      >
        {/* Header */}
        <Box
          sx={{
            px: 3,
            py: 2.25,
            background: 'linear-gradient(135deg, #1A9B7F 0%, #157763 100%)',
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
          }}
        >
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'rgba(255,255,255,0.18)',
              backdropFilter: 'blur(8px)',
              flexShrink: 0,
            }}
          >
            <TuneIcon sx={{ fontSize: 22 }} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: '1.05rem', fontWeight: 700, lineHeight: 1.2 }}>
              {dialogMode === 'add' ? 'Add Parameters' : 'Edit Parameter'}
            </Typography>
            <Typography sx={{ fontSize: '0.78rem', opacity: 0.88, mt: 0.25 }}>
              {dialogMode === 'add'
                ? 'Define parameters and the flags that can be selected during result entry.'
                : 'Update the parameter name and its available flags.'}
            </Typography>
          </Box>
          <IconButton
            onClick={handleParameterDialogClose}
            size="small"
            sx={{ color: '#fff', '&:hover': { bgcolor: 'rgba(255,255,255,0.16)' } }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <DialogContent sx={{ p: 3, bgcolor: '#f8fafc' }}>
          {/* Info banner */}
          <Box
            sx={{
              mb: 2.5,
              p: 1.5,
              borderRadius: 2,
              bgcolor: 'rgba(26,155,127,0.08)',
              border: '1px solid rgba(26,155,127,0.2)',
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1.25,
            }}
          >
            <BiotechIcon sx={{ fontSize: 18, color: '#1A9B7F', mt: '2px' }} />
            <Typography sx={{ fontSize: '0.8rem', color: '#334155', lineHeight: 1.5 }}>
              {dialogMode === 'add'
                ? 'For each parameter, list the flags or findings that should be available when entering results — e.g. '
                : 'Adjust the parameter name and the comma-separated flags that should be available — e.g. '}
              <Box component="span" sx={{ fontWeight: 700, color: '#1A9B7F' }}>normal, abnormal, high, low</Box>.
            </Typography>
          </Box>

          {parametersList.map((param, index) => (
            <Paper
              key={param.id}
              elevation={0}
              sx={{
                mb: 2,
                p: 2,
                border: '1px solid #e5e9ef',
                borderRadius: 2.5,
                bgcolor: '#fff',
                transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
                '&:hover': {
                  borderColor: 'rgba(26,155,127,0.4)',
                  boxShadow: '0 2px 8px rgba(15, 23, 42, 0.04)',
                },
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.75 }}>
                <Stack direction="row" spacing={1.25} alignItems="center">
                  <Box
                    sx={{
                      width: 26,
                      height: 26,
                      borderRadius: '50%',
                      bgcolor: 'rgba(26,155,127,0.12)',
                      color: '#1A9B7F',
                      fontWeight: 700,
                      fontSize: '0.78rem',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {index + 1}
                  </Box>
                  <Typography sx={{ fontSize: '0.92rem', fontWeight: 700, color: '#0f172a' }}>
                    Parameter {index + 1}
                  </Typography>
                </Stack>
                {parametersList.length > 1 && (
                  <Tooltip title="Remove this parameter" arrow>
                    <IconButton
                      size="small"
                      onClick={() => handleRemoveParameterField(param.id)}
                      sx={{
                        color: '#dc2626',
                        '&:hover': { bgcolor: 'rgba(220,38,38,0.08)' },
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.5px', mb: 0.75 }}>
                    Parameter Name <Box component="span" sx={{ color: '#dc2626' }}>*</Box>
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    value={param.name}
                    onChange={(e) => handleParameterChange(param.id, 'name', e.target.value)}
                    placeholder="e.g., LIVER"
                    required
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.75, bgcolor: '#fff' } }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.5px', mb: 0.75 }}>
                    Available Flags / Findings
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    value={param.description}
                    onChange={(e) => handleParameterChange(param.id, 'description', e.target.value)}
                    placeholder="normal, abnormal, high, low"
                    helperText="Separate values with commas."
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.75, bgcolor: '#fff' } }}
                  />
                </Grid>
              </Grid>
            </Paper>
          ))}

          {dialogMode === 'add' && (
            <Box sx={{ textAlign: 'center', mt: 1.5 }}>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={handleAddParameterField}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  borderRadius: 1.75,
                  borderStyle: 'dashed',
                  borderColor: 'rgba(26,155,127,0.5)',
                  color: '#1A9B7F',
                  px: 2.5,
                  '&:hover': {
                    borderStyle: 'dashed',
                    borderColor: '#1A9B7F',
                    bgcolor: 'rgba(26,155,127,0.06)',
                  },
                }}
              >
                Add Another Parameter
              </Button>
            </Box>
          )}
        </DialogContent>

        <DialogActions
          sx={{
            px: 3,
            py: 2,
            bgcolor: '#fff',
            borderTop: '1px solid #e5e9ef',
            gap: 1,
          }}
        >
          {(() => {
            const validCount = parametersList.filter(p => p.name.trim() !== '').length;
            return (
              <>
                <Typography sx={{ flex: 1, fontSize: '0.78rem', color: '#64748b' }}>
                  {validCount > 0
                    ? `${validCount} parameter${validCount === 1 ? '' : 's'} ready to save`
                    : 'Enter at least one parameter name to save'}
                </Typography>
                <Button
                  onClick={handleParameterDialogClose}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    color: '#475569',
                    px: 2.5,
                    '&:hover': { bgcolor: '#e2e8f0' },
                  }}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSaveParameter}
                  variant="contained"
                  disabled={loading || validCount === 0}
                  startIcon={loading ? <RefreshIcon /> : <SaveIcon />}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 700,
                    px: 3,
                    borderRadius: 1.75,
                    boxShadow: 'none',
                    background: 'linear-gradient(135deg, #1A9B7F 0%, #157763 100%)',
                    '&:hover': {
                      boxShadow: '0 4px 12px rgba(26, 155, 127, 0.3)',
                      background: 'linear-gradient(135deg, #157763 0%, #126553 100%)',
                    },
                  }}
                >
                  {loading
                    ? 'Saving…'
                    : dialogMode === 'add'
                      ? `Save ${validCount} Parameter${validCount === 1 ? '' : 's'}`
                      : 'Save Changes'}
                </Button>
              </>
            );
          })()}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SetupScan;